import { Box, Chip, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGrid, ISupportCase, useSelectedOrganisation } from '../../../shared';
import { useGetSupportCase } from '../../hooks';
import { ISupportCaseDuplicate } from '../../models';
import { DuplicateInfo } from '../duplicate-info/duplicate-info.component';
import { MergeSupportCases } from '../merge-support-cases/merge-support-cases.component';
import { NoDuplicateSupportCase } from '../no-duplicate/no-duplicate.component';

interface Props {
    supportCase: ISupportCase;
    duplicate: ISupportCaseDuplicate;
    onClose?: () => void;
}

export const DuplicateSubRow: FC<Props> = ({ supportCase, duplicate, onClose }) => {
    const { t } = useTranslation();
    const { organisation } = useSelectedOrganisation();
    const { shareSupportCases } = window.carity.environment;

    const hasAccess =
        shareSupportCases ||
        [...duplicate.organisations, ...duplicate.unconfirmedOrganisations].some(({ id }) => organisation?.id === id);
    const { data: supportCaseDuplicate } = useGetSupportCase(duplicate.id, { enabled: hasAccess });
    const markedAsNoDuplicate = duplicate.noDuplicates.some(({ supportCaseId }) => supportCaseId === supportCase.id);

    return (
        <FormGrid
            containerProps={{ spacing: 1 }}
            itemProps={{ '.MuiStack-root': { minHeight: '100%', width: '100%' } }}
            size={{ xs: 6 }}
        >
            <Stack spacing={1} sx={{ p: 2, borderRight: '1px solid lightGrey' }}>
                <Box>
                    <Typography variant="subtitle2">{t('currentSupportCase')}</Typography>
                    <DuplicateInfo supportCase={supportCase} />
                </Box>
                {hasAccess && (
                    <MergeSupportCases subjectId={supportCase.id} targetId={duplicate.id} onClose={onClose} />
                )}
            </Stack>

            <Stack spacing={1} sx={{ p: 2, pl: 1 }}>
                <Box>
                    <FormGrid containerProps={{ justifyContent: 'space-between', spacing: 1 }}>
                        <Typography variant="subtitle2">{t('duplicateSupportCase')}</Typography>
                        {markedAsNoDuplicate ? (
                            <Chip label={t('markedAsNoDuplicate')} />
                        ) : (
                            <NoDuplicateSupportCase
                                subjectId={supportCase.id}
                                targetId={duplicate.id}
                                onClose={onClose}
                            />
                        )}
                    </FormGrid>
                    <DuplicateInfo supportCase={supportCaseDuplicate} duplicate={duplicate} />
                </Box>
                {hasAccess && (
                    <MergeSupportCases subjectId={duplicate.id} targetId={supportCase.id} onClose={onClose} />
                )}
            </Stack>
        </FormGrid>
    );
};
